/**
 * Zod schemas for PI-QUAL scoring annotation extension.
 */
import { z } from "zod"

export const PIQUAL_BOOLEAN_FIELDS = [
  "axial_t2_snr",
  "axial_t2_delineate",
  "axial_t2_artifacts",
  "sag_coronal_t2_snr_and_artifacts",
  "dwi_contrast",
  "dwi_adc",
  "dwi_artifacts",
  "dwi_matching_adc_t2",
  "has_dce",
  "dce_artifacts",
  "dce_structures"
] as const

export const PIQUAL_NUMERIC_FIELDS = ["t2_wi_score", "dwi_score", "dce_criteria_met", "score"] as const

const piqualPayloadShape = {
  axial_t2_snr: z.boolean().optional(),
  axial_t2_delineate: z.boolean().optional(),
  axial_t2_artifacts: z.boolean().optional(),
  sag_coronal_t2_snr_and_artifacts: z.boolean().optional(),
  dwi_contrast: z.boolean().optional(),
  dwi_adc: z.boolean().optional(),
  dwi_artifacts: z.boolean().optional(),
  dwi_matching_adc_t2: z.boolean().optional(),
  has_dce: z.boolean().optional(),
  dce_artifacts: z.boolean().optional(),
  dce_structures: z.boolean().optional(),
  t2_wi_score: z.number().optional(),
  dwi_score: z.number().optional(),
  dce_criteria_met: z.number().optional(),
  score: z.number().optional()
} as const

function hasAnyPiqualValue(data: Record<string, unknown>): boolean {
  return [...PIQUAL_BOOLEAN_FIELDS, ...PIQUAL_NUMERIC_FIELDS].some((key) => data[key] !== undefined)
}

export const piqualConfigSchema = z.object({
  type: z.literal("piqual")
})

export const createPiqualSchema = z
  .object({
    project_module_id: z.string().min(1),
    series_id: z.string().nullable().optional(),
    ...piqualPayloadShape
  })
  .superRefine((data, ctx) => {
    if (!hasAnyPiqualValue(data as Record<string, unknown>)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one PI-QUAL field is required"
      })
    }
  })

export const updatePiqualSchema = z
  .object({
    ...piqualPayloadShape
  })
  .superRefine((data, ctx) => {
    if (!hasAnyPiqualValue(data as Record<string, unknown>)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one PI-QUAL field is required"
      })
    }
  })

// ---------------------------------------------------------------------------
// Completion-requirement configuration (per project_modules.config)
// ---------------------------------------------------------------------------

/**
 * User-answered boolean questions. `has_dce` is a precondition (the annotator
 * says whether DCE was acquired), so it's required, and DCE sub-questions only
 * matter when `has_dce` is true.
 */
export const PIQUAL_REQUIRABLE_FIELDS = [
  "axial_t2_snr",
  "axial_t2_delineate",
  "axial_t2_artifacts",
  "sag_coronal_t2_snr_and_artifacts",
  "dwi_contrast",
  "dwi_adc",
  "dwi_artifacts",
  "dwi_matching_adc_t2",
  "has_dce",
  "dce_artifacts",
  "dce_structures"
] as const

export type PiqualRequirableField = (typeof PIQUAL_REQUIRABLE_FIELDS)[number]

/** Default: every question is required; DCE sub-questions conditional on has_dce=true. */
export const PIQUAL_DEFAULT_REQUIRED_FIELDS: readonly PiqualRequirableField[] = PIQUAL_REQUIRABLE_FIELDS

export const piqualProjectConfigSchema = z.strictObject({
  required_fields: z.array(z.enum(PIQUAL_REQUIRABLE_FIELDS)).optional(),
  /** Escape hatch: allow completion with zero answers. */
  allow_empty: z.boolean().optional()
})

export type PiqualProjectConfig = z.infer<typeof piqualProjectConfigSchema>

/** Resolved PI-QUAL configuration for completion validation. */
export interface ResolvedPiqualConfig {
  requiredFields: readonly PiqualRequirableField[]
  allowEmpty: boolean
}

export function resolvePiqualConfig(configJson: string | null | undefined): ResolvedPiqualConfig {
  if (!configJson) {
    return { requiredFields: PIQUAL_DEFAULT_REQUIRED_FIELDS, allowEmpty: false }
  }
  try {
    const raw = JSON.parse(configJson)
    const parsed = piqualProjectConfigSchema.safeParse(raw)
    if (!parsed.success) {
      return { requiredFields: PIQUAL_DEFAULT_REQUIRED_FIELDS, allowEmpty: false }
    }
    return {
      requiredFields: parsed.data.required_fields ?? PIQUAL_DEFAULT_REQUIRED_FIELDS,
      allowEmpty: parsed.data.allow_empty ?? false
    }
  } catch {
    return { requiredFields: PIQUAL_DEFAULT_REQUIRED_FIELDS, allowEmpty: false }
  }
}

export const PIQUAL_FIELD_LABELS: Record<PiqualRequirableField, string> = {
  axial_t2_snr: "Axial T2 SNR",
  axial_t2_delineate: "Axial T2 anatomical delineation",
  axial_t2_artifacts: "Axial T2 artifacts",
  sag_coronal_t2_snr_and_artifacts: "Sagittal/coronal T2 SNR & artifacts",
  dwi_contrast: "DWI contrast",
  dwi_adc: "DWI ADC",
  dwi_artifacts: "DWI artifacts",
  dwi_matching_adc_t2: "DWI matching ADC/T2",
  has_dce: "DCE acquired?",
  dce_artifacts: "DCE artifacts",
  dce_structures: "DCE anatomical structures"
}
